<template>
  <SoDialog
    v-model="value"
    card-title="Customer Availability"
    @on-close="resetModal()"
  >
    <SoSkeleton v-if="!currentSmartMeterInterest.isFetched" height="300" />
    <ImbpRegisterInterest v-else v-model="interests" />
    <SoAlert
      v-if="isError"
      class="my-5"
      title="Failed to register smart meter interest"
      type="error"
    />
    <div class="mt-4 d-flex justify-end gap-2">
      <SoButton
        data-testid="interest-cancel-button"
        title="Cancel"
        @click="value = false"
      />
      <SoButton
        :disabled="!allowSubmit"
        :loading="isLoading"
        data-testid="interest-save-button"
        title="Save"
        type="primary"
        @click="updateSmartMeterInterest"
      />
    </div>
  </SoDialog>
</template>

<script lang="ts">
  import { ImbpSmartMeterInterest } from "@/models/imbpSmartMeterInterest/imbpSmartMeterInterest"
  import ImbpRegisterInterest from "./ImbpRegisterInterest.vue"
  import SoDialog from "@/components/SoDialog.vue"
  import SoButton from "@/components/formControls/SoButton.vue"
  import { useCurrentAccountStore } from "@/stores/currentAccount"
  import SoAlert from "@/components/SoAlert.vue"
  import SoSkeleton from "@/components/SoSkeleton.vue"
  import { errorHandler, errorReporter } from "@/services/ErrorService"
  import { ImbpMutator } from "@/mutators/imbp/ImbpMutator"
  import { useSnackbarStore } from "@/stores/snackbar"

  export default {
    components: {
      ImbpRegisterInterest,
      SoDialog,
      SoButton,
      SoAlert,
      SoSkeleton,
    },
    props: {
      modelValue: {
        type: Boolean,
        default: false,
        required: true,
      },
    },
    emits: ["update:modelValue", "success"],
    data() {
      return {
        interests: {} as ImbpSmartMeterInterest,
        current: useCurrentAccountStore(),
        snackbar: useSnackbarStore(),
        submitSmartMeterInterestMutator: ImbpMutator.submitSmartMeterInterest(),
      }
    },
    computed: {
      value: {
        get() {
          return this.modelValue
        },
        set(value: boolean) {
          this.$emit("update:modelValue", value)
        },
      },
      currentSmartMeterInterest() {
        return this.current.imbpSmartMeterInterest
      },
      allowSubmit() {
        const isRegistered = this.interests?.registered ?? false
        return (
          JSON.stringify(this.currentSmartMeterInterest.data) !==
            JSON.stringify(this.interests) && isRegistered
        )
      },
      isError() {
        return this.submitSmartMeterInterestMutator.isError
      },
      isLoading() {
        return this.submitSmartMeterInterestMutator.isPending
      },
    },
    watch: {
      modelValue(val: boolean) {
        if (val && !this.currentSmartMeterInterest.isFetched) {
          this.current.imbpSmartMeterInterest.refetch()
        }
      },
    },
    methods: {
      resetModal() {
        this.interests = {} as ImbpSmartMeterInterest
      },
      updateSmartMeterInterest() {
        try {
          const postcode = this.current.account.data.supplyAddress?.postcode
          if (!postcode || !this.current.coreId.data)
            throw new Error("Missing details for smart meter register")

          this.submitSmartMeterInterestMutator
            .mutateAsync({
              billingAccountId: this.current.coreId.data.toString(),
              postcode: postcode.toUpperCase(),
              customerInterest: this.interests,
            })
            .then(() => {
              this.current.imbpSmartMeterInterest.refetch()
              this.value = false
              this.snackbar.addSuccess(
                "Availability saved. The customer will receive an email when matching appointments become available.",
              )
              this.$emit("success")
            })
            .catch((error) => {
              errorReporter.report(error)
              this.snackbar.addError(
                errorHandler.userMessage(
                  error,
                  "Could not save the customer's availability",
                ),
              )
            })
        } catch (err) {
          errorReporter.report(err)
        }
      },
    },
  }
</script>
